import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import SavedFiltersMenu from './SavedFiltersMenu';
import { formatAppliedDateRange } from '../../lib/formatAppliedDateRange';

// ── Resource-specific config ──────────────────────────────────────────────────

/*
 * ⛔ Card P116b — THE `contacts` LIST BELOW IS NOW THE FALLBACK, NOT THE ANSWER.
 *
 * crm_contacts.pipeline_stage holds Carlos's canonical GHL stage names as well
 * as these seven short codes: routes/gohighlevel.js:1197 writes the output of
 * mapGhlStageToCanonical — the row's own crm_pipeline_stages.name — straight
 * into the column. So a hard-coded list of seven could not offer a filter for
 * most of what the column actually contains, which is what Maria hit looking
 * for "Disqualified - Not a Fit".
 *
 * ⚠⚠ THE REAL STAGES ARE ADDED, NOT SUBSTITUTED. The codes below are still
 * live values — 'new' is the column default (schema.sql:479) and the keyword
 * ladders at gohighlevel.js:1802 and :2251 still write them — so replacing
 * the list would make every contact that never reached a pipeline
 * unfilterable. Both sets, real stages first.
 *
 * ⛔ `tasks` and `opportunities` are UNTOUCHED. Task status is not a pipeline
 * stage at all, and the opportunity vocabulary is owned by
 * src/lib/opportunityStages.js under the P95c ruling.
 */
const STAGE_OPTIONS = {
  contacts: [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'converted', label: 'Converted' },
    { value: 'lost', label: 'Lost' },
    { value: 'inactive', label: 'Inactive' },
  ],
  tasks: [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ],
  opportunities: [
    { value: 'lead', label: 'Lead' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'closed_won', label: 'Closed Won' },
    { value: 'closed_lost', label: 'Closed Lost' },
  ],
};

const DATE_FIELD_OPTIONS = {
  contacts: [
    { value: 'created_at', label: 'Created Date' },
    { value: 'last_contact_date', label: 'Last Contact' },
    { value: 'next_follow_up_date', label: 'Next Follow-Up' },
  ],
  tasks: [
    { value: 'due_date', label: 'Due Date' },
    { value: 'created_at', label: 'Created Date' },
  ],
  opportunities: [
    { value: 'created_at', label: 'Created Date' },
    // ⛔ 'Close Date' removed 2026-09-07: expected_close_date is unpopulatable
    // from GHL (property absent from /opportunities/search) and measured 0 of
    // 7,803 rows. Offering a date filter that can only return an empty set is
    // the inert-affordance defect #470 removed from the board.
    { value: 'updated_at', label: 'Last Updated' },
  ],
};

const SORT_OPTIONS = {
  contacts: [
    { value: 'last_name', label: 'Last Name' },
    { value: 'first_name', label: 'First Name' },
    { value: 'created_at', label: 'Date Created' },
    { value: 'last_contact_date', label: 'Last Contact' },
    { value: 'next_follow_up_date', label: 'Next Follow-Up' },
    { value: 'pipeline_stage', label: 'Stage' },
    { value: 'owner', label: 'Owner' },
  ],
  tasks: [
    { value: 'due_date', label: 'Due Date' },
    { value: 'priority', label: 'Priority' },
    { value: 'title', label: 'Title' },
    { value: 'created_at', label: 'Date Created' },
    { value: 'status', label: 'Status' },
    { value: 'owner', label: 'Assigned To' },
  ],
  opportunities: [
    { value: 'updated_at', label: 'Last Updated' },
    { value: 'created_at', label: 'Date Created' },
    { value: 'value', label: 'Value' },
    { value: 'title', label: 'Title' },
    { value: 'stage', label: 'Stage' },
    { value: 'owner', label: 'Owner' },
    // ⛔ 'Close Date' sort removed 2026-09-07 — see the date-field list above.
  ],
};

// ── URL helpers ───────────────────────────────────────────────────────────────

function getUrlParam(key) {
  try {
    return new URLSearchParams(window.location.search).get(key) || '';
  } catch { return ''; }
}

function pushUrlParams(params) {
  try {
    const p = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(params)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    const qs = p.toString();
    history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  } catch {}
}

// Free-text search persistence — sessionStorage ONLY (tab-scoped, not
// shareable, cleared on tab close), NEVER the URL and NEVER localStorage.
// PRIVACY (HIPAA §164.312): a CRM search is routinely a patient/lead NAME, so
// it must not land in the address bar, a bookmark, a screenshot, session
// restore, or a Referer header (the URL leak this fix closes), nor persist
// across sessions on a shared workstation (why not localStorage). Keyed per
// resource so the five list views don't bleed each other's searches.
const SEARCH_STORE_PREFIX = 'zg.crmsearch.v1.';
function readSearchSession(resource) {
  try { return sessionStorage.getItem(SEARCH_STORE_PREFIX + resource) || ''; }
  catch { return ''; }
}
function writeSearchSession(resource, value) {
  try {
    if (value) sessionStorage.setItem(SEARCH_STORE_PREFIX + resource, value);
    else sessionStorage.removeItem(SEARCH_STORE_PREFIX + resource);
  } catch { /* quota/unavailable — search just won't survive refresh */ }
}

// A date range is only runnable with BOTH ends present and from <= to
// (YYYY-MM-DD strings sort chronologically). The SINGLE predicate shared by the
// Apply gating AND loadPreset, so the bar and the preset store can never drift
// on what counts as a valid, executable range.
function isValidRange(from, to) {
  return !!(from && to) && from <= to;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CrmFilterBar({
  resource = 'contacts',
  allUsers = [],
  currentUser = null,
  onFilterChange,
  className = '',
  // Opt out of the stage control. Added 2026-09-06 for the opportunity
  // pipeline board, which renders ONE COLUMN PER STAGE — "filter to stage X"
  // there is "look at column X", so the chips would be a visible control that
  // does nothing. Default false: no other caller changes.
  hideStages = false,
  /*
   * ── Card P114 (D4) ──────────────────────────────────────────────────────
   *
   * `defaultOwner` — which owner the bar STARTS on when the URL says nothing.
   * The Daily Hit List passes the signed-in user: Ryland reported it "shows
   * every rep's leads", and a daily action queue that opens on the whole
   * company is a queue nobody can work.
   *
   * ⚠⚠ IT IS A DEFAULT, NOT A LOCK, AND IT LOSES TO THE URL. `?fo=` is how a
   * shared link, a saved preset and a browser reload carry a chosen owner;
   * seeding this over the top would make a pasted link silently show the
   * wrong person's queue. So the URL wins and this fills the gap.
   *
   * ⛔ ONE SOURCE OF TRUTH. The default is seeded into the SELECT's own state,
   * not applied downstream of it — so the control and the rows can never
   * disagree. Defaulting in the consumer instead (filter the list, leave the
   * bar reading "All Owners") is the version of this fix that looks right and
   * lies.
   *
   * `ownerScope: 'self'` — offer ONLY "My Records": no "All Owners", no other
   * user. For a rep, the whole queue is theirs by definition.
   *
   * ⚠⚠ PRESENTATIONAL, AND SAID PLAINLY SO NOBODY BANKS ON IT: it is not a
   * security control. `crm:contacts:list` (routes/crm.js:1069) is gated on
   * `crm:read` alone with NO owner scoping, so every row is already in the
   * browser whatever this select offers. Real scoping has to be server-side;
   * it is proposed in the PR body and deliberately not smuggled in here.
   */
  defaultOwner = null,
  ownerScope = 'all',
}) {
  /*
   * Card P116b — the contacts stage list is DATA, fetched once per mount.
   *
   * ⛔ FAILURE FALLS BACK, IT DOES NOT EMPTY. A dropped call, an old server
   * without the channel, or a pipeline table with no rows all leave
   * `liveStages` null and the bar renders exactly the list it rendered before
   * this card. A stage control with no options is indistinguishable from "this
   * resource has no stages" and silently removes a working filter.
   */
  const [liveStages, setLiveStages] = useState(null);
  useEffect(() => {
    if (hideStages || resource !== 'contacts') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const api = window.zignagenixAPI;
        if (!api || !api.getContactStageOptions) return;
        const r = await api.getContactStageOptions({});
        const stages = (r && Array.isArray(r.stages)) ? r.stages : [];
        if (!cancelled && stages.length) {
          setLiveStages(stages.map((st) => ({ value: st.name, label: st.name })));
        }
      } catch { /* keep the fallback list */ }
    })();
    return () => { cancelled = true; };
  }, [resource, hideStages]);

  const stageOptions = useMemo(() => {
    if (hideStages) return [];
    const base = STAGE_OPTIONS[resource] || [];
    if (!liveStages || liveStages.length === 0) return base;
    // Real stages first, then any legacy code the real list does not already
    // cover. Case-insensitive, because "Qualified" and 'qualified' are one
    // filter to a rep and two entries in a naive concat.
    const seen = new Set(liveStages.map((o) => String(o.value).toLowerCase()));
    return liveStages.concat(base.filter((o) => !seen.has(String(o.value).toLowerCase())));
  }, [hideStages, resource, liveStages]);
  const dateOptions   = DATE_FIELD_OPTIONS[resource] || [];
  const sortOptions   = SORT_OPTIONS[resource]       || [];

  // Initialize from URL on mount (survives page reload)
  // ⚠ URL first, then the caller's default (Card P114 D4) — a pasted link or
  // a reload must never be overridden by a default.
  const [owner,     setOwner]     = useState(
    () => getUrlParam('fo') || (defaultOwner != null ? String(defaultOwner) : ''));
  const [stage,     setStage]     = useState(() => getUrlParam('fs'));
  const [dateField, setDateField] = useState(() => getUrlParam('fdf'));
  const [dateFrom,  setDateFrom]  = useState(() => {
    const v = getUrlParam('fdr'); return v ? v.split('_')[0] : '';
  });
  const [dateTo,    setDateTo]    = useState(() => {
    const v = getUrlParam('fdr'); return v ? (v.split('_')[1] || '') : '';
  });
  // DATEFILTER-APPLY: a date range is only meaningful with BOTH ends. The raw
  // inputs (dateFrom/dateTo) are held LOCALLY and do NOT drive the query — a
  // one-sided range would run an OPEN-ENDED query (the backend applies each
  // bound independently, database/crmFilterQuery.js). Only an explicitly
  // APPLIED, complete, valid range feeds the emit + the fdr URL param. A range
  // arriving via fdr is treated as already applied (a shared link shows AND
  // runs it). Owner/stage/sort keep their instant-apply — this gating is
  // date-range-only.
  const [appliedFrom, setAppliedFrom] = useState(() => {
    const v = getUrlParam('fdr'); return v ? v.split('_')[0] : '';
  });
  const [appliedTo,   setAppliedTo]   = useState(() => {
    const v = getUrlParam('fdr'); return v ? (v.split('_')[1] || '') : '';
  });
  const [sortBy,    setSortBy]    = useState(() => getUrlParam('fsb'));
  const [sortDir,   setSortDir]   = useState(() => getUrlParam('fsd') || 'desc');
  // Search initial state comes from tab-scoped sessionStorage, NOT the URL
  // (PHI — see readSearchSession). Structural filters above still hydrate from
  // the URL so links stay shareable/refresh-survivable.
  const [rawSearch, setRawSearch] = useState(() => readSearchSession(resource));
  const [search,    setSearch]    = useState(() => readSearchSession(resource));

  // Debounce search: 300 ms after last keystroke
  const debounceRef = useRef(null);
  const handleSearchChange = useCallback((val) => {
    setRawSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 300);
  }, []);

  // Stable ref to callback to avoid effect dependency churn
  const cbRef = useRef(onFilterChange);
  useEffect(() => { cbRef.current = onFilterChange; });

  // Emit filter + push URL whenever any filter value changes
  useEffect(() => {
    const filter = {
      owner: owner ? Number(owner) : null,
      stages: stage ? [stage] : [],
      dateField,
      dateFrom: appliedFrom,   // only the APPLIED range feeds the query (never a half-range)
      dateTo: appliedTo,
      sortBy,
      sortDir,
      search,
    };
    cbRef.current?.(filter);

    // Structural, non-identifying filters stay in the URL (shareable). The
    // free-text search does NOT: fq is always passed empty so pushUrlParams
    // DELETES it — this both stops producing ?fq= and STRIPS a legacy value
    // left by an old bookmark/shared link on mount. (We cannot retroactively
    // scrub URLs already copied or already in someone's local history — we can
    // only stop emitting new ones.) The term persists in tab-scoped
    // sessionStorage instead, for refresh-survival without the exposure.
    pushUrlParams({
      fo:  owner,
      fs:  stage,
      fdf: dateField,
      fdr: (appliedFrom || appliedTo) ? `${appliedFrom}_${appliedTo}` : '',
      fsb: sortBy,
      fsd: sortDir !== 'desc' ? sortDir : '',
      fq:  '',   // never in the URL (PHI) — always stripped
    });
    writeSearchSession(resource, search);
  }, [resource, owner, stage, dateField, appliedFrom, appliedTo, sortBy, sortDir, search]);

  // Clear date range when date field is unset (raw inputs AND applied range).
  const handleDateFieldChange = (val) => {
    setDateField(val);
    if (!val) { setDateFrom(''); setDateTo(''); setAppliedFrom(''); setAppliedTo(''); }
  };

  // Date-range apply gating. Require BOTH ends and a valid order before the
  // range can run; Apply (or Enter) is the explicit execute action. YYYY-MM-DD
  // strings compare chronologically, so a plain string compare validates order.
  const rangeComplete  = !!(dateFrom && dateTo);
  const rangeInverted  = rangeComplete && dateFrom > dateTo;
  const rangeValid     = isValidRange(dateFrom, dateTo);   // shared predicate (also used by loadPreset)
  const rangeUnapplied = dateFrom !== appliedFrom || dateTo !== appliedTo;
  const canApplyRange  = rangeValid && rangeUnapplied;
  const applyRange = () => {
    if (!rangeValid) return;              // never emit an incomplete/inverted range
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
  };
  const onRangeKeyDown = (e) => {
    if (e.key === 'Enter' && canApplyRange) { e.preventDefault(); applyRange(); }
  };

  // Unambiguous echo of the APPLIED (running) range with the month spelled —
  // sourced from appliedFrom/appliedTo, NEVER the live dateFrom/dateTo (reading
  // the live inputs would lie mid-edit, inverting #285). '' → render nothing.
  const appliedRangeEcho = formatAppliedDateRange(appliedFrom, appliedTo);

  const isActive = !!(owner || stage || dateField || dateFrom || dateTo || sortBy || search);

  const clearAll = () => {
    setOwner('');
    setStage('');
    setDateField('');
    setDateFrom('');
    setDateTo('');
    setAppliedFrom('');
    setAppliedTo('');
    setSortBy('');
    setSortDir('desc');
    setRawSearch('');
    setSearch('');
    writeSearchSession(resource, '');   // clear the tab-scoped search too
  };

  // Load a saved preset: apply all fields the preset captured, leave the rest as-is
  const loadPreset = useCallback((filterJsonb) => {
    if (!filterJsonb || typeof filterJsonb !== 'object') return;
    if ('owner'     in filterJsonb) setOwner(filterJsonb.owner ? String(filterJsonb.owner) : '');
    if ('stage'     in filterJsonb) setStage(filterJsonb.stage     || '');
    // INVARIANT: wherever the raw range is set from an EXTERNAL source, the
    // APPLIED range is set with it — a preset's range (like an fdr URL range)
    // is already-applied by definition. BUT a preset row is UNTRUSTED input:
    // crm_filter_presets rows are is_shared, so another user (possibly on an
    // older build) may have persisted a half or inverted range. So we require
    // the SAME contract as the Apply button (isValidRange): set the raw inputs
    // so the user can see/correct what the preset held, but set the APPLIED
    // range (which runs the query) ONLY when it is valid. A malformed range is
    // shown-not-run — the same posture as the legacy `search` key: ignored,
    // not honoured.
    if ('dateField' in filterJsonb) setDateField(filterJsonb.dateField || '');
    if ('dateField' in filterJsonb && !filterJsonb.dateField) {
      setDateFrom(''); setDateTo(''); setAppliedFrom(''); setAppliedTo('');
    } else if (('dateFrom' in filterJsonb) || ('dateTo' in filterJsonb)) {
      const pFrom = filterJsonb.dateFrom || '';
      const pTo   = filterJsonb.dateTo   || '';
      setDateFrom(pFrom); setDateTo(pTo);
      if (isValidRange(pFrom, pTo)) { setAppliedFrom(pFrom); setAppliedTo(pTo); }
      else { setAppliedFrom(''); setAppliedTo(''); }   // half / inverted → shown, not run
    }
    if ('sortBy'    in filterJsonb) setSortBy(filterJsonb.sortBy    || '');
    if ('sortDir'   in filterJsonb) setSortDir(filterJsonb.sortDir  || 'desc');
    // PRIVACY: saved presets are STRUCTURAL only. The free-text search is not
    // restored from a preset — a preset must never carry a patient/lead name.
    // Any legacy preset row that captured `search` is simply ignored here.
  }, []);

  // Snapshot of the current filter state — passed to SavedFiltersMenu on Save.
  // PRIVACY: `search` is deliberately EXCLUDED — presets are persisted
  // server-side (crm_filter_presets) and can be SHARED, so a typed patient/
  // lead name must never enter one. Presets capture structural filters only.
  // DATE RANGE: snapshot the APPLIED range, never the raw inputs — only a
  // range that is actually running may be saved. This closes the Save-side
  // hole (a half/inverted range typed but never applied cannot be persisted
  // and then laundered back in via loadPreset) and removes a smaller lie: a
  // saved preset always matches the list the user is looking at, not what they
  // half-typed.
  const currentFilter = useMemo(() => ({
    owner:     owner       || null,
    stage:     stage       || null,
    dateField: dateField   || null,
    dateFrom:  appliedFrom  || null,
    dateTo:    appliedTo    || null,
    sortBy:    sortBy       || null,
    sortDir:   sortDir      || 'desc',
  }), [owner, stage, dateField, appliedFrom, appliedTo, sortBy, sortDir]);

  const sel = {
    padding: '6px 8px', borderRadius: 8, border: 'none',
    background: '#F0F4F8', fontSize: 12, cursor: 'pointer',
    color: '#374151', outline: 'none', height: 32,
  };

  return (
    <div className={`crm-filter-bar${className ? ` ${className}` : ''}`}
         role="search" aria-label="Filter records">
      {/* Owner */}
      <select
        value={owner}
        onChange={e => setOwner(e.target.value)}
        aria-label="Filter by owner"
        style={sel}
      >
        {/* Card P114 (D4): a self-scoped bar offers only the signed-in user —
            for a rep every row in their queue is theirs, so "All Owners" and
            a list of colleagues are controls with nothing behind them. */}
        {ownerScope !== 'self' && <option value="">All Owners</option>}
        {currentUser && (
          <option value={String(currentUser.id)}>My Records</option>
        )}
        {ownerScope !== 'self' && allUsers
          .filter(u => u.id !== currentUser?.id)
          .map(u => (
            <option key={u.id} value={String(u.id)}>{u.display_name}</option>
          ))}
      </select>

      {/* Stage / Status */}
      {stageOptions.length > 0 && (
        <select
          value={stage}
          onChange={e => setStage(e.target.value)}
          aria-label="Filter by stage"
          style={sel}
        >
          <option value="">All Stages</option>
          {stageOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {/* Date field */}
      {dateOptions.length > 0 && (
        <select
          value={dateField}
          onChange={e => handleDateFieldChange(e.target.value)}
          aria-label="Date filter field"
          style={sel}
        >
          <option value="">No Date Filter</option>
          {dateOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {/* Date range pickers — only when dateField is set. The range does NOT
          auto-apply (a half-range would run an open-ended query); the user
          enters both ends and executes via Apply or Enter. */}
      {dateField && (
        <>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            onKeyDown={onRangeKeyDown}
            aria-label="Date from"
            className="crm-filter-bar__date"
          />
          <span style={{ color: '#9CA3AF', fontSize: 12, lineHeight: '32px' }}>–</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            onKeyDown={onRangeKeyDown}
            aria-label="Date to"
            className="crm-filter-bar__date"
          />
          <button
            type="button"
            className="crm-filter-bar__date-apply"
            onClick={applyRange}
            disabled={!canApplyRange}
            aria-label="Apply date range"
            title={
              rangeInverted ? 'From date must be on or before To date'
                : !rangeComplete ? 'Enter both dates'
                : 'Apply date range'
            }
          >
            Apply
          </button>
          {rangeInverted && (
            <span className="crm-filter-bar__date-msg" role="alert">
              From date must be on or before To date
            </span>
          )}
          {/* DATEFMT-UNAMBIGUOUS: month-spelled echo of the APPLIED range (see
              appliedRangeEcho above). In-page only; never a nav label (#279). */}
          {appliedRangeEcho ? (
            <span className="crm-filter-bar__applied-range" aria-live="polite">{appliedRangeEcho}</span>
          ) : null}
        </>
      )}

      {/* Sort */}
      {sortOptions.length > 0 && (
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label="Sort by"
          style={sel}
        >
          <option value="">Default Sort</option>
          {sortOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {sortBy && (
        <button
          type="button"
          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
          aria-label={`Sort direction: ${sortDir}`}
          className="crm-filter-bar__sort-dir"
        >
          {sortDir === 'asc' ? '↑' : '↓'}
        </button>
      )}

      {/* Search */}
      <div className="crm-filter-bar__search">
        <Search size={13} className="crm-filter-bar__search-icon" aria-hidden="true" />
        <input
          type="text"
          placeholder="Search…"
          value={rawSearch}
          onChange={e => handleSearchChange(e.target.value)}
          aria-label="Search"
          className="crm-filter-bar__search-input"
        />
        {rawSearch && (
          <button
            type="button"
            onClick={() => handleSearchChange('')}
            aria-label="Clear search"
            className="crm-filter-bar__search-clear"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Clear all */}
      {isActive && (
        <button
          type="button"
          onClick={clearAll}
          aria-label="Clear all filters"
          className="crm-filter-bar__clear"
        >
          Clear
        </button>
      )}

      {/* Saved presets — only for server-backed resources */}
      {['contacts', 'tasks', 'opportunities'].includes(resource) && (
        <SavedFiltersMenu
          resource={resource}
          currentFilter={currentFilter}
          onLoad={loadPreset}
        />
      )}
    </div>
  );
}
